import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatMoney } from "@/lib/api";
import { Kicker, CATEGORIES } from "@/components/common";
import CameraCapture from "@/components/CameraCapture";
import { UploadCloud, CheckCircle2, Loader2, ArrowLeft, Camera } from "lucide-react";

export default function AddExpense() {
  const nav = useNavigate();
  const [form, setForm] = useState({
    amount: "",
    currency: "CAD",
    category: "Other",
    customer: "",
    notes: "",
    merchant: "",
    date: new Date().toISOString().slice(0, 10),
  });
  const [receiptB64, setReceiptB64] = useState("");
  const [receiptPreview, setReceiptPreview] = useState("");
  const [scanning, setScanning] = useState(false);
  const [scanBanner, setScanBanner] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);
  const fileRef = useRef(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const readFileAsDataURL = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const handleFile = async (file) => {
    if (!file) return;
    if (!/image\/(jpeg|png|webp|jpg)/i.test(file.type)) {
      setError("Please upload a JPEG, PNG, or WEBP image.");
      return;
    }
    setError("");
    setScanBanner("");
    const dataUrl = await readFileAsDataURL(file);
    setReceiptPreview(dataUrl);
    setReceiptB64(dataUrl);

    setScanning(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/expenses/scan-receipt", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const d = res.data || {};
      const filled = [];
      setForm((f) => {
        const next = { ...f };
        if (d.amount) { next.amount = String(d.amount); filled.push("amount"); }
        if (d.currency) { next.currency = d.currency; filled.push("currency"); }
        if (d.category) { next.category = d.category; filled.push("category"); }
        if (d.merchant) { next.merchant = d.merchant; filled.push("merchant"); }
        if (d.date) { next.date = d.date; filled.push("date"); }
        return next;
      });
      if (filled.length) {
        setScanBanner(`Auto-filled: ${filled.join(", ")}`);
      } else {
        setScanBanner("Scanned — no fields could be confidently extracted. Please fill manually.");
      }
    } catch (e) {
      setError(e?.response?.data?.detail || "Receipt scan failed");
    } finally {
      setScanning(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  };

  const submit = async (e) => {
    e?.preventDefault();
    setError("");
    const amount = parseFloat(form.amount);
    if (!amount || amount <= 0) {
      setError("Enter a valid amount");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/expenses", {
        amount,
        currency: form.currency,
        category: form.category,
        customer: form.customer || null,
        notes: form.notes || null,
        merchant: form.merchant || null,
        date: form.date,
        receipt_base64: receiptB64 || null,
      });
      nav("/expenses", { replace: true });
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to submit");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-6 py-8 animate-fade-up">
      <button
        data-testid="add-back-btn"
        onClick={() => nav(-1)}
        className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-950 mb-4"
      >
        <ArrowLeft className="w-4 h-4" /> Back
      </button>
      <Kicker>New expense</Kicker>
      <h1 className="font-display text-3xl font-semibold tracking-tight text-zinc-950 mt-1 mb-8">Submit an expense</h1>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Receipt upload */}
        <div className="lg:col-span-2">
          <div
            data-testid="dropzone"
            onDragEnter={(e) => { e.preventDefault(); setDragActive(true); }}
            onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
            onDragLeave={() => setDragActive(false)}
            onDrop={onDrop}
            className={`dropzone ${dragActive ? "active" : ""} rounded-xl p-6 bg-white min-h-[300px] flex flex-col items-center justify-center text-center`}
          >
            {receiptPreview ? (
              <div className="w-full">
                <img
                  data-testid="receipt-preview"
                  src={receiptPreview}
                  alt="Receipt preview"
                  className="w-full max-h-72 object-contain rounded-lg border border-zinc-100"
                />
                <div className="mt-3 flex items-center justify-center gap-3 text-sm">
                  <button
                    type="button"
                    data-testid="replace-receipt-btn"
                    onClick={() => fileRef.current?.click()}
                    className="text-zinc-600 hover:text-zinc-950"
                  >
                    Replace file
                  </button>
                  <span className="text-zinc-300">·</span>
                  <button
                    type="button"
                    data-testid="replace-camera-btn"
                    onClick={() => setCameraOpen(true)}
                    className="inline-flex items-center gap-1 text-zinc-600 hover:text-zinc-950"
                  >
                    <Camera className="w-3.5 h-3.5" /> Retake
                  </button>
                </div>
              </div>
            ) : (
              <>
                <UploadCloud className="w-8 h-8 text-zinc-400" />
                <div className="mt-3 font-display text-lg font-medium text-zinc-950">Drag & drop a receipt</div>
                <p className="text-sm text-zinc-500 mt-1 max-w-xs">We'll scan it with AI and auto-fill the form.</p>
                <div className="mt-4 flex items-center gap-2">
                  <button
                    type="button"
                    data-testid="browse-file-btn"
                    onClick={() => fileRef.current?.click()}
                    className="inline-flex items-center rounded-lg border border-zinc-200 bg-white px-3 py-1.5 text-sm hover:bg-zinc-50"
                  >
                    Choose file
                  </button>
                  <button
                    type="button"
                    data-testid="open-camera-btn"
                    onClick={() => setCameraOpen(true)}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-zinc-950 text-white px-3 py-1.5 text-sm font-medium hover:bg-zinc-800"
                  >
                    <Camera className="w-3.5 h-3.5" /> Use camera
                  </button>
                </div>
                <p className="text-xs text-zinc-400 mt-3">JPEG · PNG · WEBP · up to 8MB</p>
              </>
            )}
            <input
              ref={fileRef}
              data-testid="receipt-file-input"
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="hidden"
              onChange={(e) => handleFile(e.target.files?.[0])}
            />
          </div>

          {scanning ? (
            <div data-testid="scan-loader" className="mt-3 inline-flex items-center gap-2 text-sm text-zinc-600">
              <Loader2 className="w-4 h-4 animate-spin" /> Scanning receipt…
            </div>
          ) : null}
          {scanBanner && !scanning ? (
            <div data-testid="scan-banner" className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-700 text-sm px-3 py-2 inline-flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 mt-0.5" /> <span>{scanBanner}</span>
            </div>
          ) : null}
        </div>

        {/* Form */}
        <form onSubmit={submit} className="lg:col-span-3 rounded-xl border border-zinc-200 bg-white p-6 space-y-5">
          <div className="grid grid-cols-3 gap-4">
            <div className="col-span-2">
              <label className="text-xs font-medium text-zinc-600">Amount</label>
              <input
                data-testid="add-amount-input"
                type="number"
                step="0.01"
                min="0"
                value={form.amount}
                onChange={(e) => set("amount", e.target.value)}
                required
                className="mt-1 w-full rounded-lg border border-zinc-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950/20 focus:border-zinc-950 font-display text-lg tabular-nums"
                placeholder="0.00"
              />
              {form.amount ? (
                <div className="mt-1 text-xs text-zinc-400">{formatMoney(form.amount, form.currency)}</div>
              ) : null}
            </div>
            <div>
              <label className="text-xs font-medium text-zinc-600">Currency</label>
              <div className="mt-1 grid grid-cols-2 gap-1 rounded-lg border border-zinc-200 p-1 bg-zinc-50">
                {["CAD", "USD"].map((c) => (
                  <button
                    type="button"
                    key={c}
                    data-testid={`currency-${c}-btn`}
                    onClick={() => set("currency", c)}
                    className={`rounded-md py-1.5 text-sm font-medium ${
                      form.currency === c ? "bg-white text-zinc-950 shadow-sm" : "text-zinc-500 hover:text-zinc-800"
                    }`}
                  >
                    {c}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-zinc-600">Category</label>
            <div className="mt-1 flex flex-wrap gap-2">
              {CATEGORIES.map((c) => (
                <button
                  type="button"
                  key={c}
                  data-testid={`category-chip-${c}`}
                  onClick={() => set("category", c)}
                  className={`px-3 py-1.5 rounded-full border text-sm transition-colors ${
                    form.category === c
                      ? "bg-zinc-950 text-white border-zinc-950"
                      : "bg-white text-zinc-700 border-zinc-200 hover:bg-zinc-50"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-medium text-zinc-600">Merchant</label>
              <input
                data-testid="add-merchant-input"
                value={form.merchant}
                onChange={(e) => set("merchant", e.target.value)}
                className="mt-1 w-full rounded-lg border border-zinc-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950/20 focus:border-zinc-950"
                placeholder="Starbucks"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-zinc-600">Date</label>
              <input
                data-testid="add-date-input"
                type="date"
                value={form.date}
                onChange={(e) => set("date", e.target.value)}
                className="mt-1 w-full rounded-lg border border-zinc-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950/20 focus:border-zinc-950"
              />
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-zinc-600">Customer <span className="text-zinc-400">(optional)</span></label>
            <input
              data-testid="add-customer-input"
              value={form.customer}
              onChange={(e) => set("customer", e.target.value)}
              className="mt-1 w-full rounded-lg border border-zinc-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950/20 focus:border-zinc-950"
              placeholder="Acme Corp"
            />
          </div>

          <div>
            <label className="text-xs font-medium text-zinc-600">Notes <span className="text-zinc-400">(optional)</span></label>
            <textarea
              data-testid="add-notes-input"
              value={form.notes}
              onChange={(e) => set("notes", e.target.value)}
              rows={3}
              className="mt-1 w-full rounded-lg border border-zinc-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950/20 focus:border-zinc-950"
              placeholder="Client dinner with…"
            />
          </div>

          {error ? (
            <div data-testid="add-error" className="rounded-lg border border-red-200 bg-red-50 text-red-700 text-sm px-3 py-2">
              {error}
            </div>
          ) : null}

          <div className="flex items-center justify-end gap-2 pt-2">
            <button
              type="button"
              data-testid="add-cancel-btn"
              onClick={() => nav(-1)}
              className="inline-flex items-center rounded-lg border border-zinc-200 bg-white px-4 py-2.5 text-sm hover:bg-zinc-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              data-testid="add-submit-btn"
              disabled={submitting}
              className="inline-flex items-center rounded-lg bg-zinc-950 text-white px-4 py-2.5 text-sm font-medium hover:bg-zinc-800 disabled:opacity-60"
            >
              {submitting ? "Submitting…" : "Submit expense"}
            </button>
          </div>
        </form>
      </div>

      <CameraCapture
        open={cameraOpen}
        onClose={() => setCameraOpen(false)}
        onCapture={(file) => handleFile(file)}
      />
    </div>
  );
}
